import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { HKPImage } from "../../../media/HKPImage";
import { canUseOrders } from "../../../helpers/orderEnabled";
import { canUseSalesApproval } from "../../../helpers/salesApprovalEnabled";
import { returnByLabel } from "./ApprovalMemo";
import { OverdueParcelsChip } from "./ParcelsCard";
import { listSalesMemos } from "../../../services/studioSalesApi";
import { geoRecordId, unwrapGeoPayload } from "../../gi-registry/geoPath";
import { formatInr, memoTotals, memoValueLines } from "../salesInvoice";
import { hasPhoto, phoneStatus, photoProps, shortDate } from "./salesPhoneModel";
import "./salesPhone.scss";

const LIMIT = 20;
type Kind = "SALE" | "CREDIT" | "APPROVAL";
const CHIPS: Array<{ key: string; label: string }> = [
  { key: "READY_FOR_BILLING", label: "To bill" },
  { key: "DRAFT", label: "Drafts" },
  { key: "INVOICE_GENERATED", label: "Done" },
  { key: "", label: "All" },
];

/** Approval memos: what is out with buyers comes first. */
const APPROVAL_CHIPS: Array<{ key: string; label: string }> = [
  { key: "OUT_ON_APPROVAL", label: "Out" },
  { key: "DRAFT", label: "Drafts" },
  { key: "CLOSED", label: "Closed" },
  { key: "", label: "All" },
];

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const parseList = (res: any) => {
  const payload = unwrapGeoPayload(res) || {};
  return {
    docs: Array.isArray(payload.docs) ? payload.docs : [],
    length: Number(payload.length) || 0,
  };
};

const SalesPhoneHub = () => {
  const history = useHistory();
  const role = readRole();
  const canSee = canUseOrders(role);
  const canWrite = canSee && role !== "SUPER_ADMIN";
  const approvalOn = canUseSalesApproval(role);
  const kinds: Kind[] = approvalOn ? ["SALE", "CREDIT", "APPROVAL"] : ["SALE", "CREDIT"];
  const [kind, setKind] = useState<Kind>("SALE");
  const [status, setStatus] = useState("READY_FOR_BILLING");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<Kind, number>>({ SALE: 0, CREDIT: 0, APPROVAL: 0 });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const request = useRef(0);

  const load = useCallback(async (nextPage: number) => {
    const ticket = ++request.current;
    setLoading(true);
    setError("");
    try {
      const res: any = await listSalesMemos({ offset: nextPage, limit: LIMIT, type: kind, status, q: q.trim() });
      if (ticket !== request.current) return;
      if (!res?.success) {
        setError(res?.message || "Could not load memos");
        return;
      }
      const parsed = parseList(res);
      setRows((prior) => (nextPage === 0 ? parsed.docs : [...prior, ...parsed.docs]));
      setTotal(parsed.length);
      setPage(nextPage);
    } catch {
      if (ticket === request.current) setError("Could not load memos. Check your connection.");
    } finally {
      if (ticket === request.current) setLoading(false);
    }
  }, [kind, q, status]);

  useEffect(() => {
    if (!canSee) {
      history.replace("/dashboard");
      return;
    }
    const wait = window.setTimeout(() => void load(0), q ? 300 : 0);
    return () => window.clearTimeout(wait);
  }, [canSee, history, load, q]);

  useEffect(() => {
    Promise.all([
      listSalesMemos({ offset: 0, limit: 1, type: "SALE" }),
      listSalesMemos({ offset: 0, limit: 1, type: "CREDIT" }),
      approvalOn ? listSalesMemos({ offset: 0, limit: 1, type: "APPROVAL", status: "OUT_ON_APPROVAL" }) : Promise.resolve(null),
    ]).then(([sale, credit, approval]: any[]) => {
      setCounts({
        SALE: sale?.success ? parseList(sale).length : 0,
        CREDIT: credit?.success ? parseList(credit).length : 0,
        APPROVAL: approval?.success ? parseList(approval).length : 0,
      });
    }).catch(() => undefined);
  }, [approvalOn]);

  const pickKind = (value: Kind) => {
    setKind(value);
    // Each kind opens on its own "needs you" list; the other chips stay one tap away.
    setStatus(value === "APPROVAL" ? "OUT_ON_APPROVAL" : "READY_FOR_BILLING");
  };
  const chips = kind === "APPROVAL" ? APPROVAL_CHIPS : CHIPS;

  const open = (row: any, confirm = false) =>
    history.push(`/sales/memos/${geoRecordId(row)}${confirm ? "?confirm=1" : ""}`);

  return (
    <div className="sp is-hub">
      <div className="sp-head">
        <span className="t13 grow">Memos become invoices in your billing software.</span>
        {canWrite ? (
          <button type="button" className="sp-link" onClick={() => history.push("/sales/take-back")}>Take it back</button>
        ) : null}
      </div>
      <div className="sp-seg" role="tablist">
        {kinds.map((value) => (
          <button key={value} type="button" role="tab" aria-selected={kind === value}
            className={kind === value ? "is-on" : ""} onClick={() => pickKind(value)}>
            {value === "SALE" ? "Invoices" : value === "CREDIT" ? "Credit notes" : "On approval"} · {counts[value]}
          </button>
        ))}
      </div>
      <OverdueParcelsChip role={role} />
      <input className="sp-search" type="search" value={q} placeholder="Party, memo or invoice no."
        aria-label="Search memos" onChange={(e) => setQ(e.target.value)} />
      <div className="sp-chips">
        {chips.map((chip) => (
          <button key={chip.key || "all"} type="button" className={`sp-chip${status === chip.key ? " is-on" : ""}`}
            onClick={() => setStatus(chip.key)}>
            {chip.key === "READY_FOR_BILLING" && kind === "CREDIT" ? "To credit" : chip.label}
          </button>
        ))}
      </div>
      {error ? (
        <div className="sp-warn" role="alert">
          {error} <button type="button" className="sp-link" onClick={() => load(0)}>Retry</button>
        </div>
      ) : null}
      <div className="sp-list">
      {rows.map((row) => {
        const lines = memoValueLines(row);
        const st = phoneStatus(row.status, row.type);
        const doc = row.creditNoteNumber || row.invoiceNumber;
        return (
          <div key={geoRecordId(row)} className="sp-card">
            <button type="button" className="sp-card" style={{ border: 0, padding: 0 }} onClick={() => open(row)}>
              <div className="sp-row" style={{ width: "100%" }}>
                <div className="grow">
                  <div className="t16 clip">{row.partyName || "No party yet"}</div>
                  <div className="t13 clip">
                    <span className="mono">{row.memoCode || "Draft"}</span>
                    {` · ${lines.length} ${lines.length === 1 ? "piece" : "pieces"}`}
                    {doc ? ` · ${doc}` : ""}
                    {row.updatedAt ? ` · ${shortDate(row.updatedAt)}` : ""}
                  </div>
                </div>
                {row.type === "APPROVAL" && row.status === "OUT_ON_APPROVAL" && row.returnBy ? (
                  // What the studio needs at a glance: when the pieces are due back.
                  <span className={`sp-st is-${row.overdue ? "bad" : "ready"}`}>
                    {row.overdue ? "Overdue · " : "Back by "}{returnByLabel(row.returnBy)}
                  </span>
                ) : <span className={`sp-st is-${st.tone}`}>{st.label}</span>}
              </div>
              <div className="sp-row" style={{ width: "100%" }}>
                <div className="sp-thumbs">
                  {lines.slice(0, 3).map((line: any) => {
                    const photo = photoProps(line.thumbnail);
                    return hasPhoto(photo) ? (
                      <HKPImage key={line.id} className="sp-th" preset="THUMBNAIL" {...photo}
                        alt="" loading="lazy" sizes="34px" />
                    ) : <span key={line.id} className="sp-th" />;
                  })}
                  {lines.length > 3 ? <span className="t13">+{lines.length - 3}</span> : null}
                </div>
                <div className="grow" />
                <div className="sp-amt">{formatInr(memoTotals(lines).total)}</div>
              </div>
            </button>
            {canWrite && row.status === "READY_FOR_BILLING" ? (
              <div className="sp-row sp-card__foot">
                <span className="t13 grow">
                  {row.type === "CREDIT" ? "Credit note made in your software?" : "Billed in your software?"}
                </span>
                <button type="button" className="sp-link" onClick={() => open(row, true)}>
                  {row.type === "CREDIT" ? "Add CN no." : "Add invoice no."}
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
      </div>
      {!loading && !error && rows.length === 0 ? (
        <p className="t13 sp-empty">
          {q ? "Nothing matches that search." : kind === "SALE" ? "No memos here yet." : kind === "CREDIT" ? "No credit notes here yet."
            : "Nothing out on approval. Send pieces to a buyer to decide at their place."}
        </p>
      ) : null}
      {loading ? <p className="t13 sp-empty" role="status">Loading…</p> : null}
      {!loading && rows.length < total ? (
        <button type="button" className="sp-btn" onClick={() => load(page + 1)}>Show more</button>
      ) : null}
      {canWrite ? (
        <div className="sp-bar">
          <div className="sp-btns">
            {kind === "APPROVAL" ? (
              <button type="button" className="sp-btn is-pri" onClick={() => history.push("/sales/approval/new")}>+ Send on approval</button>
            ) : (
              <>
                <button type="button" className="sp-btn" onClick={() => history.push("/sales/credit/new")}>↺ Credit note</button>
                <button type="button" className="sp-btn is-pri" onClick={() => history.push("/sales/memos/new")}>+ New memo</button>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SalesPhoneHub;
